import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import api from "../components/setupAxios";
import API_URL from "../config";
import { Box, Grid, Card, CardContent, Typography, Button, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';

const LayoutDesign = () => {
  const { labId } = useParams();
  const [lab, setLab] = useState(null);
  const [newPod, setNewPod] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (labId) {
      fetchLabDetails();
    }
  }, [labId]);

  const fetchLabDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/lab/${labId}`);
      setLab(response.data);
    } catch (error) {
      console.error("Error fetching lab details:", error);
    }
  };

  const createPod = async () => {
    if (!newPod) return;
    try {
      if (newPod === "professor-desk") {
        const hasProfessorDesk = lab.pods.some((pod) => pod.direction === "professor-desk");
        if (hasProfessorDesk) {
          alert("Only one professor desk is allowed per lab!");
          setNewPod(null);
          return;
        }
      }
      let newPodId;
      if (newPod === "professor-desk") {
        newPodId = "professor-desk";
      } else {
        const existingPodNumbers = lab.pods
          .filter((pod) => pod.direction !== "professor-desk")
          .map((pod) => parseInt(pod.podId))
          .sort((a, b) => a - b);
        let smallestAvailable = 1;
        while (existingPodNumbers.includes(smallestAvailable)) {
          smallestAvailable++;
        }
        newPodId = smallestAvailable.toString();
      }
      await api.put("/lab/add-pod", {
        labId,
        podId: newPodId,
        x: 0,
        y: 0,
        direction: newPod,
      },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      fetchLabDetails();
      setNewPod(null);
    } catch (error) {
      console.error("Error creating pod:", error);
    }
  };

  const deletePod = async (podId) => {
    try {
      await api.put("/lab/remove-pod", { labId, podId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      fetchLabDetails();
    } catch (error) {
      console.error("Error deleting pod:", error);
    }
  };

  const toggleSeatAvailability = async (podId, seatId) => {
    try {
      await api.put("/lab/toggle-seat-availability", { labId, podId, seatId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      fetchLabDetails();
    } catch (error) {
      console.error("Error toggling seat availability:", error);
    }
  };

  const startDragging = (e, pod) => {
    setDragging(pod.podId);
    setDragOffset({ x: e.clientX - pod.x, y: e.clientY - pod.y });
  };

  const onDragging = (e) => {
    if (!dragging) return;
    const updatedPods = lab.pods.map((pod) => {
      if (pod.podId === dragging) {
        return { ...pod, x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y };
      }
      return pod;
    });
    setLab({ ...lab, pods: updatedPods });
  };

  const stopDragging = async () => {
    if (!dragging) return;
    const pod = lab.pods.find((p) => p.podId === dragging);
    try {
      await axios.put(`${API_URL}/lab/update-pod-position`, {
        labId,
        podId: pod.podId,
        x: pod.x,
        y: pod.y,
      },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
    } catch (error) {
      console.error("Error updating pod position:", error);
    }
    setDragging(null);
  };

  // PodCard component: handles hover state and shows overlay with Delete button.
  // When mouse is over seat buttons, overlay is hidden.
  const PodCard = ({ pod }) => {
    const [hovered, setHovered] = useState(false);
    const [seatHovered, setSeatHovered] = useState(false);
  
    const renderPodLayoutWithSeatHover = (pod) => {
      switch (pod.direction) {
        case "horizontal-left":
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                Pod-{pod.podId}
              </Typography>
              <Box
                onMouseEnter={() => setSeatHovered(true)}
                onMouseLeave={() => setSeatHovered(false)}
              >
                <Box sx={{ display: 'flex' }}>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[0].seatId)}
                    sx={{
                      backgroundColor: pod.seats[0].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[0].seatId}
                  </Button>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[1].seatId)}
                    sx={{
                      backgroundColor: pod.seats[1].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[1].seatId}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', mt: 1 }}>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[2].seatId)}
                    sx={{
                      backgroundColor: pod.seats[2].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[2].seatId}
                  </Button>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[3].seatId)}
                    sx={{
                      backgroundColor: pod.seats[3].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[3].seatId}
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        case "horizontal-right":
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                onMouseEnter={() => setSeatHovered(true)}
                onMouseLeave={() => setSeatHovered(false)}
              >
                <Box sx={{ display: 'flex' }}>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[0].seatId)}
                    sx={{
                      backgroundColor: pod.seats[0].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[0].seatId}
                  </Button>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[1].seatId)}
                    sx={{
                      backgroundColor: pod.seats[1].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[1].seatId}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', mt: 1 }}>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[2].seatId)}
                    sx={{
                      backgroundColor: pod.seats[2].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[2].seatId}
                  </Button>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[3].seatId)}
                    sx={{
                      backgroundColor: pod.seats[3].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[3].seatId}
                  </Button>
                </Box>
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1 }}>
                Pod-{pod.podId}
              </Typography>
            </Box>
          );
        case "vertical":
          return (
            <Box>
              <Typography variant="subtitle2" align="center">
                Pod-{pod.podId}
              </Typography>
              <Box
                onMouseEnter={() => setSeatHovered(true)}
                onMouseLeave={() => setSeatHovered(false)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[0].seatId)}
                    sx={{
                      backgroundColor: pod.seats[0].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[0].seatId}
                  </Button>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[2].seatId)}
                    sx={{
                      backgroundColor: pod.seats[2].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[2].seatId}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[1].seatId)}
                    sx={{
                      backgroundColor: pod.seats[1].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[1].seatId}
                  </Button>
                  <Button
                    variant="text"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggleSeatAvailability(pod.podId, pod.seats[3].seatId)}
                    sx={{
                      backgroundColor: pod.seats[3].status === "Available" ? 'green' : 'red',
                      color: 'white',
                      minWidth: '30px'
                    }}
                  >
                    {pod.seats[3].seatId}
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        case "professor-desk":
          return (
            <Box sx={{ width: '120px', height: '50px', backgroundColor: 'lightblue', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <Typography variant="subtitle2" align="center">
                Professor's Desk
              </Typography>
            </Box>
          );
        default:
          return null;
      }
    };
  
    return (
      <Box
        sx={{
          position: 'absolute',
          left: pod.x,
          top: pod.y,
          cursor: 'grab',
          p: 1,
        }}
        onMouseDown={(e) => startDragging(e, pod)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setSeatHovered(false); }}
      >
        <Card variant="outlined" sx={{ border: 'none', boxShadow: 1, p: 1, position: 'relative' ,backgroundColor:'rgba(207, 206, 206, 0.8)'}}>
          {renderPodLayoutWithSeatHover(pod)}
          {hovered && !seatHovered && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 0, 0, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              <Button
                variant="contained"
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  deletePod(pod.podId);
                }}
                sx={{ p: 0, pointerEvents: 'auto' }}
              >
                Delete
              </Button>
            </Box>
          )}
        </Card>
      </Box>
    );
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Grid container spacing={2} sx={{ width: '80%', height: '600px' }}>
        {/* Left Column: Two Cards */}
        <Grid item xs={4} sx={{ height: '100%' }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
            }}
          >
            {/* Lab Information Card */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Layout for {lab?.name}</Typography>
                <Typography variant="body2">Location: {lab?.location}</Typography>
                <Typography variant="body2">Capacity: {lab?.capacity}</Typography>
              </CardContent>
            </Card>
            {/* Pod Controls Card */}
            <Card variant="outlined">
              <CardContent>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Select Pod Type</FormLabel>
                  <RadioGroup
                    name="podType"
                    value={newPod || ""}
                    onChange={(e) => setNewPod(e.target.value)}
                  >
                    <FormControlLabel value="horizontal-left" control={<Radio />} label="Horizontal Left" />
                    <FormControlLabel value="horizontal-right" control={<Radio />} label="Horizontal Right" />
                    <FormControlLabel value="vertical" control={<Radio />} label="Vertical" />
                    <FormControlLabel value="professor-desk" control={<Radio />} label="Professor Desk" />
                  </RadioGroup>
                </FormControl>
                <Box mt={2}>
                  <Button variant="contained" fullWidth onClick={createPod}>
                    Create Pod
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
        {/* Right Column: Canvas */}
        <Grid item xs={8} sx={{ height: '100%' }}>
          <Box
            sx={{
              width: '800px',
              height: '600px',
              border: '1px solid #ddd',
              p: 2,
              borderRadius: 2,
              boxShadow: 1,
              position: 'relative',
              backgroundColor:'white'
            }}
            onMouseMove={onDragging}
            onMouseUp={stopDragging}
          >
            {lab?.pods.map((pod) => (
              <PodCard key={pod.podId} pod={pod} />
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LayoutDesign;
